import { Loader2 } from 'lucide-react';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';

export interface VersionSummary {
  id: string;
  version: number;
  created_by_email: string;
  created_at: string;
  change_note: string;
}

interface VersionHistoryPanelProps {
  versions: VersionSummary[] | undefined;
  isLoading: boolean;
  isError: boolean;
  selectedVersion: number | null;
  onSelectVersion: (version: number) => void;
  onCloseVersion: () => void;
  versionDetail?: React.ReactNode;
  isLoadingDetail?: boolean;
}

export function VersionHistoryPanel({
  versions,
  isLoading,
  isError,
  selectedVersion,
  onSelectVersion,
  onCloseVersion,
  versionDetail,
  isLoadingDetail = false,
}: VersionHistoryPanelProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-500" role="status">
        <Loader2 className="size-4 animate-spin" />
        Loading version history…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-md border border-red-101 bg-red-101 p-3 text-sm text-red-700" role="alert">
        Could not load version history.
      </div>
    );
  }

  if (!versions?.length) {
    return (
      <p className="text-sm text-gray-500">No version history yet.</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {versions.map((version) => (
          <button
            key={version.id}
            type="button"
            onClick={() => onSelectVersion(version.version)}
            className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
              selectedVersion === version.version
                ? 'border-black bg-gray-100'
                : 'border-gray-300 bg-white hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-black">Version {version.version}</span>
              <span className="text-xs text-gray-500">{formatDisplayDateTime(version.created_at)}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500">{version.created_by_email}</p>
            {version.change_note ? (
              <p className="mt-1 text-sm text-gray-700">{version.change_note}</p>
            ) : (
              <p className="mt-1 text-xs italic text-gray-400">No change note</p>
            )}
          </button>
        ))}
      </div>

      {selectedVersion !== null ? (
        <section className="rounded-md border border-gray-300 bg-gray-50 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h4 className="text-sm font-medium text-black">Version {selectedVersion} snapshot</h4>
            <SchedulerDsButton type="button" variant="secondary" size="sm" onClick={onCloseVersion}>
              Close
            </SchedulerDsButton>
          </div>
          {isLoadingDetail ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="size-4 animate-spin" />
              Loading version…
            </div>
          ) : (
            versionDetail ?? <p className="text-sm text-gray-500">Select a version to view its payload.</p>
          )}
        </section>
      ) : null}
    </div>
  );
}
